import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Truck, Wifi, WifiOff, Clock } from 'lucide-react';
import { PageContainer } from '@/components/common/PageContainer';
import { useDriverTracking, DriverLocation } from '@/hooks/useDriverTracking';
import { useI18n } from '@/contexts/I18nContext';

export default function DriverTracking() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const hasFittedRef = useRef(false);
  const { drivers, connected } = useDriverTracking();
  const [selected, setSelected] = useState<string | null>(null);
  const { tp } = useI18n();

  function timeAgo(timestamp: string): string {
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (diff < 60) return `${diff} ${tp.drivers.secondsUnit}`;
    if (diff < 3600) return `${Math.floor(diff / 60)} ${tp.drivers.minuteUnit}`;
    return `${Math.floor(diff / 3600)} ${tp.drivers.hourUnit}`;
  }

  const truckIcon = (isSelected: boolean) =>
    L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: ${isSelected ? '#f59e0b' : '#22c55e'};width: 36px;height: 36px;border-radius: 50%;display: flex;align-items: center;justify-content: center;border: 3px solid white;box-shadow: 0 2px 8px rgba(0,0,0,0.3);transition: background-color 0.3s;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.684-.949V8a1 1 0 0 1 1-1h1.382a1 1 0 0 1 .894.553l1.448 2.894A1 1 0 0 0 19.382 11H20a2 2 0 0 1 2 2v4a1 1 0 0 1-1 1h-1"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg></div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
    });

  useEffect(() => {
    if (!mapContainer.current || map.current) return;
    map.current = L.map(mapContainer.current).setView([15.5007, 32.5599], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 }).addTo(map.current);
    return () => { map.current?.remove(); map.current = null; markersRef.current = {}; };
  }, []);

  useEffect(() => {
    if (!map.current) return;
    const currentIds = new Set<string>();
    drivers.forEach((loc: DriverLocation, driverId: string) => {
      currentIds.add(driverId);
      const existing = markersRef.current[driverId];
      if (existing) { existing.setLatLng([loc.lat, loc.lng]); existing.setIcon(truckIcon(driverId === selected)); }
      else {
        const marker = L.marker([loc.lat, loc.lng], { icon: truckIcon(driverId === selected) }).addTo(map.current!);
        marker.bindPopup(`<div style="direction: rtl; text-align: right; min-width: 160px;"><strong>${tp.drivers.delegateLabel}: ${driverId}</strong><br/><hr style="margin: 6px 0; border-color: #eee;"/><span>${tp.drivers.latitudeLabel}: ${loc.lat.toFixed(5)}</span><br/><span>${tp.drivers.longitudeLabel}: ${loc.lng.toFixed(5)}</span><br/><span>${tp.drivers.lastUpdateLabel}: ${new Date(loc.timestamp).toLocaleTimeString('ar')}</span></div>`);
        marker.on('click', () => setSelected(driverId));
        markersRef.current[driverId] = marker;
      }
    });
    Object.keys(markersRef.current).forEach((id) => { if (!currentIds.has(id)) { markersRef.current[id].remove(); delete markersRef.current[id]; } });
    const markerValues = Object.values(markersRef.current);
    if (markerValues.length > 0 && !hasFittedRef.current) {
      const group = L.featureGroup(markerValues);
      map.current.fitBounds(group.getBounds(), { padding: [50, 50], maxZoom: 13 });
      hasFittedRef.current = true;
    }
  }, [drivers, selected]);

  const driverList = Array.from(drivers.entries());
  const flyToDriver = (driverId: string, loc: DriverLocation) => { setSelected(driverId); map.current?.flyTo([loc.lat, loc.lng], 14, { duration: 1 }); markersRef.current[driverId]?.openPopup(); };

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl font-bold">{tp.drivers.trackingPageTitle}</h1>
          <Badge variant={connected ? 'default' : 'destructive'} className="gap-1">
            {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {connected ? tp.drivers.connectedStatus : tp.drivers.disconnectedStatus}
          </Badge>
        </div>
      </div>
      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block w-64 overflow-y-auto space-y-2">
          <Card className="p-3"><div className="flex items-center gap-2 text-sm font-medium"><Truck className="h-4 w-4" /><span>{tp.drivers.activeDriversLabel}: {driverList.length}</span></div></Card>
          {driverList.length === 0 && <Card className="p-4 text-center text-sm text-muted-foreground">{tp.drivers.noActiveDrivers}</Card>}
          {driverList.map(([driverId, loc]) => (
            <Card key={driverId} className={`p-3 cursor-pointer transition-colors hover:bg-accent ${selected === driverId ? 'ring-2 ring-primary' : ''}`} onClick={() => flyToDriver(driverId, loc)}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center"><Truck className="h-4 w-4 text-white" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{driverId}</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /><span>{tp.drivers.sinceLabel} {timeAgo(loc.timestamp)}</span></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
        <div className="flex-1 min-w-0 relative">
          <Card className="overflow-hidden h-full"><div ref={mapContainer} className="h-full w-full rounded-lg" /></Card>
          <div className="absolute top-4 right-4 z-[1000]">
            <Card className="p-3 bg-background/95 backdrop-blur-sm">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-green-500" /><span className="font-medium">{driverList.length}</span><span className="text-muted-foreground">{tp.drivers.activeDriverStat}</span></div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
